/**
 * attachmentStorage.ts
 *
 * File URL resolution and download utilities for the Cloudflare R2 system.
 *
 * All file URLs are resolved to full Cloudflare Worker URLs:
 *   https://billowing-star-0889.salim-zarepour.workers.dev/files/{r2Key}
 *
 * IndexedDB functions are kept for text extraction use only —
 * they are NOT used as a file URL source or fallback storage.
 */

import { getWorkerFileUrl } from './workerFileStorage';
import Tesseract from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

// Configure pdfjs worker in browser environment
if (typeof window !== 'undefined' && pdfjsLib) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version || '4.10.38'}/build/pdf.worker.min.mjs`;
  } catch (e) {
    console.warn('pdfjs worker configuration warning:', e);
  }
}

const DB_NAME = 'vaxa_attachments_db';
const DB_VERSION = 1;
const STORE_NAME = 'attachments_data';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB not supported'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Saves a data URL to IndexedDB (used for text extraction only, not file storage). */
export async function saveAttachmentBlob(id: string, dataUrl: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(dataUrl, id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('Failed to save attachment blob to IndexedDB:', err);
  }
}

/** Reads a data URL from IndexedDB (used for text extraction only, not file URL resolution). */
export async function getAttachmentBlob(id: string): Promise<string | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('Failed to read attachment blob from IndexedDB:', err);
    return null;
  }
}

/** Deletes a data URL from IndexedDB. */
export async function deleteAttachmentBlob(id: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('Failed to delete attachment blob from IndexedDB:', err);
  }
}

/**
 * Resolves any file reference into a full Cloudflare Worker URL.
 *
 * Priority:
 *  1. If key is provided → build Worker URL from key
 *  2. If fileUrl is already a Worker URL → return as-is
 *  3. If fileUrl contains a key param → extract and build Worker URL
 *  4. If fileUrl is an /uploads/ legacy path → convert key → Worker URL
 *  5. If fileUrl is a full https:// link (e.g. video embed) → return as-is
 *  6. Fallback: treat fileId/fileUrl as the R2 key
 *
 * Note: No IndexedDB, no blob URLs, no /api/r2/ paths.
 */
export async function resolveSafeFileUrl(fileUrl: string, fileId?: string, key?: string): Promise<string> {
  // 1. Explicit R2 key provided — highest priority
  if (key) {
    return getWorkerFileUrl(key);
  }

  // 2. Already a valid Worker /files/ URL
  if (fileUrl?.includes('workers.dev/files/')) {
    return fileUrl;
  }

  // 3. Contains ?key= param (old /api/r2/file?key= style) — extract key
  if (fileUrl?.includes('key=')) {
    try {
      const keyParam = new URLSearchParams(fileUrl.split('?')[1]).get('key');
      if (keyParam) return getWorkerFileUrl(keyParam);
    } catch (_) {}
  }

  // 4. Legacy /uploads/ path — convert to R2 key
  if (fileUrl?.includes('/uploads/')) {
    const parts = fileUrl.split('/uploads/');
    if (parts[1]) {
      return getWorkerFileUrl(`uploads/${parts[1]}`);
    }
  }

  // 5. External https:// URL (video links, external embeds) — return as-is
  if (fileUrl?.startsWith('http://') || fileUrl?.startsWith('https://')) {
    return fileUrl;
  }

  // 6. Fallback: treat fileId or fileUrl as the R2 key
  const fallbackKey = fileId || fileUrl;
  if (fallbackKey && !fallbackKey.startsWith('data:') && !fallbackKey.startsWith('blob:')) {
    return getWorkerFileUrl(fallbackKey);
  }

  return '';
}

/**
 * Returns a synchronous, browser-safe URL for preview links or iframe sources.
 * Use this when you already have the resolved Worker URL.
 */
export function getSafeBrowserUrl(resolvedUrl: string, fallbackUrl?: string): string {
  const target = resolvedUrl || fallbackUrl || '';
  if (!target || target === '#') {
    return '#';
  }
  return target;
}

/**
 * Triggers a file download using the Cloudflare Worker URL.
 * Uses the r2Key to build the direct download URL.
 */
export async function triggerFileDownload(fileUrl: string, fileName: string, fileId?: string, key?: string): Promise<void> {
  // Build the Worker URL — prefer explicit key, then resolve from fileUrl
  let downloadUrl = '';

  if (key) {
    downloadUrl = getWorkerFileUrl(key);
  } else {
    downloadUrl = await resolveSafeFileUrl(fileUrl, fileId, key);
  }

  if (!downloadUrl) {
    console.warn('triggerFileDownload: could not resolve download URL');
    return;
  }

  // Append ?download=true so the Worker sets Content-Disposition: attachment
  const finalUrl = downloadUrl.includes('?')
    ? `${downloadUrl}&download=true`
    : `${downloadUrl}?download=true`;

  try {
    const a = document.createElement('a');
    a.href = finalUrl;
    a.download = fileName || 'download';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
    }, 300);
  } catch (err) {
    console.warn('Download trigger failed:', err);
    window.open(finalUrl, '_blank');
  }
}

/**
 * Extracts plain text content from a PDF data URL or array buffer.
 * Used for AI text analysis — NOT for file URL resolution.
 */
export function extractTextFromDataUrl(dataUrl: string, fileName: string): string {
  try {
    if (!dataUrl) return '';
    if (dataUrl.startsWith('data:text/') || dataUrl.startsWith('data:application/json')) {
      const base64Index = dataUrl.indexOf(';base64,');
      if (base64Index !== -1) {
        const base64 = dataUrl.substring(base64Index + 8);
        return decodeURIComponent(escape(atob(base64)));
      }
    }

    if (dataUrl.includes('data:application/pdf') || fileName.toLowerCase().endsWith('.pdf')) {
      const base64Index = dataUrl.indexOf(';base64,');
      if (base64Index !== -1) {
        const base64 = dataUrl.substring(base64Index + 8);
        const binaryString = atob(base64.substring(0, Math.min(base64.length, 500000)));
        const matches: string[] = [];
        const regex = /\(([^()]{2,100})\)\s*(?:Tj|TJ|\')/g;
        let match;
        while ((match = regex.exec(binaryString)) !== null) {
          if (match[1] && match[1].trim().length > 1) {
            const clean = match[1].replace(/[^\x20-\x7E\u0600-\u06FF\u00C0-\u017F]/g, ' ').trim();
            if (clean.length > 2) matches.push(clean);
          }
        }
        if (matches.length > 5) return matches.join(' ');
      }
    }
  } catch (err) {
    console.warn('Text extraction failed:', err);
  }
  return '';
}

/**
 * Directly extracts text content from an image via browser Tesseract OCR with timeout guard.
 */
export async function extractTextFromImage(imageSource: File | Blob | string): Promise<string> {
  const ocrPromise = (async () => {
    try {
      const result = await Tesseract.recognize(
        imageSource,
        'swe+eng',
        {
          errorHandler: (e) => console.warn('Tesseract OCR warning:', e)
        }
      );
      return result?.data?.text || '';
    } catch (err) {
      console.warn('OCR with swe+eng failed, trying eng fallback:', err);
      try {
        const fallbackResult = await Tesseract.recognize(imageSource, 'eng');
        return fallbackResult?.data?.text || '';
      } catch (err2) {
        console.warn('Fallback OCR error:', err2);
        return '';
      }
    }
  })();

  // 18s timeout guard so OCR never hangs user actions indefinitely
  const timeoutPromise = new Promise<string>((resolve) => {
    setTimeout(() => {
      console.warn('OCR timeout reached (18s)');
      resolve('');
    }, 18000);
  });

  return Promise.race([ocrPromise, timeoutPromise]);
}

/**
 * Extracts text content from a PDF document (File, Blob, ArrayBuffer, or URL) using PDF.js.
 * Preserves table row structure by grouping items according to Y-coordinates.
 * If text stream is empty (scanned image PDF), renders pages to canvas and performs OCR.
 */
export async function extractTextFromPdf(pdfInput: ArrayBuffer | Blob | File | string): Promise<string> {
  try {
    let arrayBuffer: ArrayBuffer;
    if (pdfInput instanceof File || pdfInput instanceof Blob) {
      arrayBuffer = await pdfInput.arrayBuffer();
    } else if (typeof pdfInput === 'string') {
      if (pdfInput.startsWith('data:')) {
        const base64 = pdfInput.split(',')[1];
        const binaryStr = atob(base64);
        const len = binaryStr.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }
        arrayBuffer = bytes.buffer;
      } else {
        const res = await fetch(pdfInput);
        arrayBuffer = await res.arrayBuffer();
      }
    } else {
      arrayBuffer = pdfInput;
    }

    let pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;
    try {
      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        useSystemFonts: true
      } as any);
      pdfDoc = await loadingTask.promise;
    } catch (loadErr) {
      console.warn('PDF.js loading with worker failed, trying without worker:', loadErr);
      try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = '';
        const loadingTask2 = pdfjsLib.getDocument({
          data: new Uint8Array(arrayBuffer)
        });
        pdfDoc = await loadingTask2.promise;
      } catch (loadErr2) {
        console.warn('PDF.js loading completely failed:', loadErr2);
      }
    }

    const textPieces: string[] = [];

    if (pdfDoc) {
      const numPages = Math.min(pdfDoc.numPages, 10);

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        let pageHasText = false;

        if (textContent && textContent.items && textContent.items.length > 0) {
          // Group text items by line (using Y coordinate transform[5])
          const lineMap: { [y: number]: { x: number; text: string }[] } = {};

          for (const item of textContent.items as any[]) {
            if (!item.str || !item.str.trim()) continue;
            // Round Y coordinate to nearest 4 points to group items on same line
            const y = Math.round((item.transform ? item.transform[5] : 0) / 4) * 4;
            const x = item.transform ? item.transform[4] : 0;
            if (!lineMap[y]) lineMap[y] = [];
            lineMap[y].push({ x, text: item.str.trim() });
          }

          // Sort lines top-to-bottom (PDF Y is bottom-to-top, so higher Y is higher on page)
          const sortedYs = Object.keys(lineMap).map(Number).sort((a, b) => b - a);
          const pageLines: string[] = [];

          for (const y of sortedYs) {
            // Sort items on the line left-to-right
            const lineItems = lineMap[y].sort((a, b) => a.x - b.x);
            const lineStr = lineItems.map(it => it.text).join('   ');
            if (lineStr.trim()) {
              pageLines.push(lineStr);
            }
          }

          const pageText = pageLines.join('\n');
          if (pageText.trim().length > 15) {
            textPieces.push(pageText);
            pageHasText = true;
          }
        }

        // If no native text found on page, render to Canvas for OCR
        if (!pageHasText && typeof document !== 'undefined') {
          try {
            const viewport = page.getViewport({ scale: 2.0 }); // 2.0 resolution for clear OCR
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            if (context) {
              await (page.render as any)({ canvasContext: context, viewport }).promise;
              const ocrText = await extractTextFromImage(canvas.toDataURL('image/png'));
              if (ocrText && ocrText.trim().length > 10) {
                textPieces.push(ocrText);
              }
            }
          } catch (ocrPdfErr) {
            console.warn(`OCR rendering warning on PDF page ${pageNum}:`, ocrPdfErr);
          }
        }
      }
    }

    const resultText = textPieces.join('\n\n').trim();
    if (resultText && resultText.length > 10) {
      return resultText;
    }

    // Ultimate Raw PDF Stream Regex Fallback
    const bytes = new Uint8Array(arrayBuffer.slice(0, Math.min(arrayBuffer.byteLength, 1000000)));
    let binaryString = '';
    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i]);
    }
    const matches: string[] = [];
    const regex = /\(([^()]{2,150})\)\s*(?:Tj|TJ|\')/g;
    let match;
    while ((match = regex.exec(binaryString)) !== null) {
      if (match[1] && match[1].trim().length > 1) {
        const clean = match[1].replace(/[^\x20-\x7E\u0600-\u06FF\u00C0-\u017F]/g, ' ').trim();
        if (clean.length > 2) matches.push(clean);
      }
    }
    if (matches.length > 3) {
      return matches.join('\n');
    }

    return '';
  } catch (err) {
    console.warn('PDF text extraction completely failed:', err);
    return '';
  }
}

/**
 * Directly extracts text content from a File object (text, json, markdown, pdf, images).
 */
export async function extractTextFromFile(file: File): Promise<string> {
  try {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (['txt', 'json', 'md', 'csv', 'js', 'ts', 'html', 'css', 'py', 'c', 'cpp', 'java', 'xml'].includes(ext) || file.type.startsWith('text/')) {
      return await file.text();
    }
    if (file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'tiff'].includes(ext)) {
      return await extractTextFromImage(file);
    }
    if (ext === 'pdf' || file.type.includes('pdf')) {
      const pdfText = await extractTextFromPdf(file);
      if (pdfText && pdfText.length > 5) return pdfText;
    }
  } catch (err) {
    console.warn('extractTextFromFile failed:', err);
  }
  return '';
}
